// Settings › Agents and tokens › Access tokens (D101; APS-310): the tokens CI, bots, the CLI and
// the MCP bridge read this project with. Owners and admins create one in a dialog (a name, write
// scopes only if chosen, 7 to 365 days) and see it once, there, to copy; Apsio keeps its id,
// never the token. Members see the list. A revoke takes effect within 30 seconds.
import { useQuery } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  orgQuery,
  projectTokensQuery,
  useCreateProjectToken,
  useRevokeProjectToken,
} from '../api/queries'
import type { NewProjectToken } from '../api/types'
import { CodeBlock } from '../components/CodeBlock'
import { Confirm } from '../components/Confirm'
import { Btn, btnClass, EmptyNote, Section, Table } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { initials } from '../lib/format'

const SCOPES = ['dashboards:write', 'issues:write'] as const
type Scope = (typeof SCOPES)[number]
const LIFETIMES = [7, 30, 90, 365] as const
type Lifetime = (typeof LIFETIMES)[number]

const SCOPE_LABEL = {
  'dashboards:write': 'agents.tokens.scope.dashboards',
  'issues:write': 'agents.tokens.scope.issues',
} as const

export function AccessTokens({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t, date, relative } = useI18n()
  const org = useQuery(orgQuery(orgId))
  const list = useQuery(projectTokensQuery(orgId, projectId))
  const revoke = useRevokeProjectToken(orgId, projectId)
  const admin = org.data?.role === 'owner' || org.data?.role === 'admin'
  const when = (value: string | null) =>
    value ? date(value, { dateStyle: 'medium' }) : t('agents.tokens.never')
  // The token's own scope words, as the API and the CLI name them: every token reads.
  const scopeText = (scopes: readonly string[]) => ['read', ...scopes].join(', ')
  const rows = list.data ?? []
  return (
    <Section
      title={t('agents.tokens')}
      meta={t('agents.tokens.shownOnce')}
      right={admin ? <CreateToken orgId={orgId} projectId={projectId} /> : undefined}
    >
      {admin ? null : <p className="se-hint">{t('agents.tokens.membersSee')}</p>}
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorState error={list.error} retry={() => list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyNote title={t('agents.tokens.none')}>{t('agents.tokensSub')}</EmptyNote>
      ) : (
        <Table label={t('agents.tokens.table')}>
          <thead>
            <tr>
              <th className="f">{t('agents.tokens.col.name')}</th>
              <th>{t('agents.tokens.col.createdBy')}</th>
              <th>{t('agents.tokens.col.scopes')}</th>
              <th>{t('agents.tokens.col.lastUsed')}</th>
              <th className={admin ? undefined : 'last'}>{t('agents.tokens.col.expires')}</th>
              {admin ? (
                <th className="last">
                  <span className="visually-hidden">{t('settings.keys.actions')}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="f wrap">
                  <code translate="no">…{row.id.slice(-4)}</code>
                  <span className="t2">{row.name}</span>
                </td>
                <td>
                  {row.created_by_name ? (
                    <span className="se-who">
                      <span className="avatar" aria-hidden="true">
                        {initials(row.created_by_name, '')}
                      </span>
                      {row.created_by_name}
                    </span>
                  ) : (
                    t('agents.tokens.byApsio')
                  )}
                </td>
                <td>
                  <code translate="no">{scopeText(row.scopes)}</code>
                </td>
                <td>{row.last_used_at ? relative(row.last_used_at) : t('agents.tokens.never')}</td>
                <td className={admin ? undefined : 'last'}>{when(row.expires_at)}</td>
                {admin ? (
                  <td className="last">
                    <Confirm
                      buttonClass={btnClass('ghost', 'sm')}
                      label={t('agents.tokens.revoke')}
                      title={t('agents.tokens.revokeTitle')}
                      body={t('agents.tokens.revokeBody', { name: row.name })}
                      confirm={t('agents.tokens.revoke')}
                      busy={revoke.isPending}
                      onConfirm={() => revoke.mutate(row.id)}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {revoke.error ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
    </Section>
  )
}

/** Create a token, in a dialog that then shows it once. */
function CreateToken({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t, date, rich } = useI18n()
  const create = useCreateProjectToken(orgId, projectId)
  const ref = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<Scope[]>([])
  const [days, setDays] = useState<Lifetime>(90)
  const [made, setMade] = useState<NewProjectToken>()
  const ids = { title: useId(), name: useId(), days: useId(), scopes: useId() }
  const ready = name.trim().length >= 1 && name.trim().length <= 100
  const close = () => {
    ref.current?.close()
    setMade(undefined)
  }
  return (
    <>
      <Btn
        variant="ghost"
        size="sm"
        onClick={() => {
          create.reset()
          setMade(undefined)
          ref.current?.showModal()
        }}
      >
        {t('agents.tokens.create')}
      </Btn>
      <dialog ref={ref} aria-labelledby={ids.title} onClose={() => setMade(undefined)}>
        {made ? (
          <div className="stack" style={{ gap: 14 }}>
            <h2 id={ids.title} style={{ fontSize: 17 }}>
              {t('agents.tokens.theToken')}
            </h2>
            <p className="notice ok" role="status">
              {rich('agents.tokens.madeOnce', {
                name: made.name,
                date: date(made.expires_at, { dateStyle: 'medium' }),
              })}
            </p>
            {/* Its own width, not the token's: the dialog stays on screen and the token scrolls. */}
            <div style={{ minWidth: 0 }}>
              <CodeBlock label={t('agents.tokens.theToken')} lines={[made.token]} />
            </div>
            <div className="head-actions">
              <button type="button" className="btn solid" onClick={close}>
                {t('agents.tokens.done')}
              </button>
            </div>
          </div>
        ) : (
          <form
            className="stack"
            style={{ gap: 14 }}
            aria-labelledby={ids.title}
            onSubmit={(e) => {
              e.preventDefault()
              if (!ready) return
              create.mutate(
                { name: name.trim(), scopes, days },
                {
                  onSuccess: (token) => {
                    setMade(token)
                    setName('')
                    setScopes([])
                    setDays(90)
                  },
                },
              )
            }}
          >
            <h2 id={ids.title} style={{ fontSize: 17 }}>
              {t('agents.tokens.create')}
            </h2>
            <p className="dim" style={{ fontSize: 13.5 }}>
              {t('agents.tokensSub')}
            </p>
            <div className="field">
              <label htmlFor={ids.name}>{t('agents.tokens.name')}</label>
              <input
                id={ids.name}
                className="input"
                maxLength={100}
                autoComplete="off"
                placeholder={t('agents.tokens.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <fieldset className="field">
              <legend id={ids.scopes}>{t('agents.tokens.scopes')}</legend>
              <p className="hint">{t('agents.tokens.scopesHint')}</p>
              {SCOPES.map((s) => (
                <label key={s} className="se-chk">
                  <input
                    type="checkbox"
                    checked={scopes.includes(s)}
                    onChange={(e) =>
                      setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))
                    }
                  />
                  {t(SCOPE_LABEL[s])}
                </label>
              ))}
            </fieldset>
            <div className="field">
              <label htmlFor={ids.days}>{t('agents.tokens.lifetime')}</label>
              <select
                id={ids.days}
                className="input"
                value={days}
                onChange={(e) => setDays(Number(e.target.value) as Lifetime)}
              >
                {LIFETIMES.map((d) => (
                  <option key={d} value={d}>
                    {t('agents.tokens.days', { days: d })}
                  </option>
                ))}
              </select>
            </div>
            {create.error ? (
              <p role="alert" className="notice error">
                {isApiError(create.error, 400)
                  ? t('agents.tokens.invalid')
                  : isApiError(create.error, 403)
                    ? t('agents.tokens.forbidden')
                    : t('state.failed')}
              </p>
            ) : null}
            <div className="head-actions">
              <button type="submit" className="btn solid" disabled={!ready || create.isPending}>
                {t('agents.tokens.create')}
              </button>
              <button type="button" className="btn" onClick={close}>
                {t('settings.cancel')}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  )
}
